import { queryOptions } from "@tanstack/react-query"
import { safeQueryOptions } from "@src/utils/query"
import { client } from "./client"
import { components } from "./schema"
import { UnpackPromise, UnpackArray } from "@src/utils/types"

export const userKeys = {
  all: ["users"],
  list: () => [...userKeys.all, "list"],
  current: () => [...userKeys.all, "current"],
  currentStuff: () => [...userKeys.current(), "stuff"],
  communities: () => [...userKeys.all, "current", "communities"],
  singleProfile: (id?: number) => [...userKeys.all, "profile", id],
  usersList: (id?: number) => [...userKeys.all, "list", "community", id],
  search: (filter?: UserSearchFilter) => [...userKeys.all, "search", filter],
  activateAccount: (code?: string | null) => [
    ...userKeys.all,
    "activate-account",
    code,
  ],
}

export type LoginRequest = components["schemas"]["LoginRequestDto"]
export type SignupRequest = components["schemas"]["SignUpDto"]

export type TokenResponse = {
  access_token: string
  username: string
  token_type: string
}

export const getCurrentUser = async () => {
  const data = await client.GET("/api/User/GetCurrent")

  return data
}

export type CurrentUser = UnpackPromise<ReturnType<typeof getCurrentUser>>

export const getCurrentUserQuery = () =>
  queryOptions({
    queryKey: userKeys.current(),
    queryFn: getCurrentUser,
    // 24 hour cache time
    gcTime: 1000 * 60 * 60 * 24,
    // 1 minute stale time (time before background refresh)
    staleTime: 1000 * 60,
  })

export const getCurrentUserCommunities = async () => {
  const data = await client.GET("/api/User/GetMyCommunities")

  return data
}

export type UserCommunitiesList = UnpackPromise<
  ReturnType<typeof getCurrentUserCommunities>
>
export type UserCommunities = UnpackArray<UserCommunitiesList>

export const getCurrentUserCommunitiesQuery = () =>
  queryOptions({
    queryKey: userKeys.communities(),
    queryFn: getCurrentUserCommunities,
  })

export const getToken = async (
  request: LoginRequest,
): Promise<TokenResponse> => {
  const data = await client.POST("/token", {
    body: request,
  })

  return data as unknown as TokenResponse
}

export const getCurrentUserStuff = async () => {
  const data = await client.GET("/api/User/GetMyStuff")

  return data
}

export type CurrentUserStuff = UnpackPromise<
  ReturnType<typeof getCurrentUserStuff>
>

export const getCurrentUserStuffQuery = () =>
  queryOptions({
    queryKey: userKeys.currentStuff(),
    queryFn: getCurrentUserStuff,
  })

export const getUserProfile = async (id: number) => {
  const data = await client.GET("/api/User/GetProfile", {
    params: {
      query: {
        id,
      },
    },
  })

  return data
}

export type UserProfile = UnpackPromise<ReturnType<typeof getUserProfile>>

export const getUserProfileQuery = (id?: number) =>
  safeQueryOptions({
    queryKeys: userKeys.singleProfile(id),
    queryFn: getUserProfile,
    unsafeQueryFnArgs: [id],
  })

export type ExternalUserInvitationRequest =
  components["schemas"]["ExternalUserInvitationDto"]

export const inviteToProjectViaEmail = async (
  inviteRequest: ExternalUserInvitationRequest,
) => {
  const data = await client.POST("/api/Project/InviteToProjectViaEmail", {
    body: inviteRequest,
  })

  return data
}

export const inviteToCommunityViaEmail = async (
  inviteRequest: ExternalUserInvitationRequest,
) => {
  const data = await client.POST("/api/User/InviteToCommunityViaEmail", {
    body: inviteRequest,
  })

  return data
}

export const inviteToTabEntityViaEmail = async (
  inviteRequest: ExternalUserInvitationRequest,
) => {
  const data = await client.POST("/api/TabEntity/InviteToTabEntityViaEmail", {
    body: inviteRequest,
  })

  return data
}

export const followUser = async (userId: number, communityId?: number) => {
  await client.GET("/api/User/Follow", {
    params: {
      query: {
        id: userId,
        ...(communityId !== undefined && { communityId }),
      },
    },
  })
}

export const unfollowUser = async (userId: number, communityId?: number) => {
  await client.GET("/api/User/Unfollow", {
    params: {
      query: {
        id: userId,
        ...(communityId !== undefined && { communityId }),
      },
    },
  })
}

export const changeUserProjectCircle = async (
  userId: number,
  projectId: number,
  projectCircleId: number,
) => {
  await client.GET("/api/Project/EditProjectCircle", {
    params: {
      query: {
        userId,
        projectId,
        projectCircleId,
      },
    },
  })
}

export const changeUserCommunityCircle = async (
  userId: number,
  communityId: number,
  communityCircleId: number,
) => {
  await client.GET("/api/Community/EditCommunityCircle", {
    params: {
      query: {
        userId,
        communityId,
        communityCircleId,
      },
    },
  })
}

export const removeUserFromProject = async (
  userId: number,
  projectId: number,
) => {
  await client.GET("/api/Project/AbandonProject", {
    params: {
      query: {
        userId,
        projectId,
      },
    },
  })
}

export const userSignup = async (payload: SignupRequest) => {
  await client.POST("/api/User/SignUp", {
    body: payload,
  })
}

export const removeUserFromCommunity = async (
  userId: number,
  communityId: number,
) => {
  await client.GET("/api/Community/AbandonCommunity", {
    params: {
      query: {
        userId,
        communityId,
      },
    },
  })
}

export const userSignupViaApplication = async (
  payload: SignupRequest,
  applicationDefinitionId: number,
) => {
  await client.POST("/api/User/SignUpViaApplication", {
    body: payload,
    params: {
      query: {
        appId: applicationDefinitionId,
      },
    },
  })
}

export const removeUserFromTabEntity = async (
  userId: number,
  entityId: number,
) => {
  await client.POST("/api/TabEntity/{id}/AbandonTabEntity", {
    params: {
      path: {
        id: entityId,
      },
      query: {
        userId,
      },
    },
  })
}

export type UserSearchFilter = components["schemas"]["UserSearchFilter"]

export type UserForInviteDtoResultListDto =
  components["schemas"]["UserForInviteDtoResultListDto"]

export type UserForInviteDto = components["schemas"]["UserForInviteDto"]

export const searchUsers = async (filter: UserSearchFilter) => {
  const data = await client.POST("/api/User/Search", {
    body: filter,
  })

  return data as unknown as UserForInviteDtoResultListDto
}

export const searchUsersQuery = (filter: UserSearchFilter) =>
  queryOptions({
    queryFn: () => searchUsers(filter),
    queryKey: userKeys.search(filter),
  })

export const resetPassword = async (email: string) => {
  const data = await client.GET("/api/User/SendResetPasswordMail", {
    params: {
      query: {
        mail: email,
      },
    },
  })

  return data
}

export const activateAccount = async (code: string) => {
  const data = await client.GET("/api/User/Activate", {
    params: {
      query: {
        code,
      },
    },
  })

  return data
}

export const activateAccountQuery = (code?: string | null) => ({
  ...safeQueryOptions({
    queryFn: activateAccount,
    unsafeQueryFnArgs: [code],
    queryKeys: userKeys.activateAccount(code),
  }),
  retry: false,
  refetchOnMount: false,
})

export const confirmResetPassword = async (
  activationCode: string,
  password: string,
) => {
  const data = await client.GET("/api/User/ResetPassword", {
    params: {
      query: {
        activationCode,
        password,
      },
    },
  })

  return data
}

export type EditUserTagsDto = components["schemas"]["EditUserTagsDto"]

export const saveUserTags = async (request: EditUserTagsDto) => {
  await client.POST("/api/User/SaveTags", {
    body: request,
  })
}

export type SaveUserProfileDto = components["schemas"]["SaveUserProfileDto"]

export const saveUserProfile = async (request: SaveUserProfileDto) => {
  await client.POST("/api/User/SaveProfile", {
    body: request,
  })
}

export type ChangePasswordDto = components["schemas"]["ChangePasswordDto"]

export const changePassword = async (request: ChangePasswordDto) => {
  await client.POST("/api/User/ChangePassword", {
    body: request,
  })
}

export type EmailEditDto = components["schemas"]["EmailEeditDto"]

export const editMyEmail = async (request: EmailEditDto) => {
  await client.POST("/api/User/EditMyEmail", {
    body: request,
  })
}

export const disableMe = async () => {
  await client.GET("/api/User/DisableMe")
}

export const requestPersonalInformation = async () => {
  const response = await client.axiosInstance.get(
    "/api/UserPersonalInformation/RequestPersonalInformation",
    { responseType: "blob" },
  )
  return response.data
}

// Admin functions
export type User = components["schemas"]["User"]
export type UserHistoryDataDto = components["schemas"]["UserHistoryDataDto"]
export type ExternalUserPrefilledInvitationDto =
  components["schemas"]["ExternalUserPrefilledInvitationDto"]

export const getAllUsers = async () => {
  const data = await client.GET("/api/User/GetAll")
  return data
}

export const getAllUsersQuery = () =>
  queryOptions({
    queryKey: userKeys.list(),
    queryFn: getAllUsers,
  })

export const banUser = async (id: number) => {
  await client.GET("/api/User/BanUser", {
    params: {
      query: {
        id,
      },
    },
  })
}

export const disableUser = async (id: number) => {
  await client.GET("/api/User/DisableUser", {
    params: {
      query: {
        id,
      },
    },
  })
}

export const togglePlatformAdmin = async (id: number) => {
  await client.GET("/api/User/TogglePlatformAdmin", {
    params: {
      query: {
        id,
      },
    },
  })
}

export const getUserHistoryData = async (id: number) => {
  const data = await client.GET("/api/UserHistoryData/Get", {
    params: {
      query: {
        id,
      },
    },
  })
  return data
}

export type BulkInvitee = ExternalUserPrefilledInvitationDto

export const signUpImportUsers = async (invitees: BulkInvitee[]) => {
  await client.POST("/api/User/SignUpImport", {
    body: invitees,
  })
}

export const createBulkUsers = async (
  invitees: BulkInvitee[],
  onProgress?: (completed: number, failed: number) => void,
) => {
  let completed = 0
  let failed = 0

  const results = await Promise.all(
    invitees.map(async (invitee) => {
      try {
        await client.POST("/api/User/SignUpImport", {
          body: [invitee],
        })
        completed++
        onProgress?.(completed, failed)
        return { success: true, email: invitee.email }
      } catch (error) {
        failed++
        onProgress?.(completed, failed)
        return { success: false, email: invitee.email, error }
      }
    }),
  )

  return results
}

export const uploadUserUpdateCsv = async (file: File, communityId: number) => {
  const formData = new FormData()
  formData.append("file", file)

  const response = await client.axiosInstance.post(
    `/api/File/UserUpdateCsv/${communityId}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  )
  return response.data
}
